import { Box, Container, Heading, Text } from '@chakra-ui/react'

import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import { getAboutUrl } from '@/client/services/url'
import { getArticleUrl, getUrl } from '@/client/services/url/faq'

import Layout from '@/components/Layout/Layout'
import ShwLink from '@/components/ShwLink'

const TermsOfService: React.FC = () => (
  <Layout title="Terms of Service">
    <Container maxWidth="768px" textAlign="justify">
      <Text color="fg.muted" mb="24px" textAlign="right">
        Last Updated: 10.25.2024
      </Text>
      <Heading textAlign="center" size="2xl" mb="24px" style={{ fontVariant: 'small-caps' }}>
        SCHOOLHOUSE.WORLD Terms of Service
      </Heading>

      <Text mb="24px">
        Please read these Terms of Service (the &quot;<u>Agreement</u>&quot;) carefully. Your use of
        the SHW Platform (as defined below) constitutes your consent to this Agreement.
      </Text>

      <Text mb="24px">
        This Agreement is between you and Schoolhouse.World, Inc. (&quot;<u>SHW</u>&quot; or &quot;
        <u>we</u>&quot; or &quot;<u>us</u>&quot;) concerning your use of the SHW website located at{' '}
        <ShwLink isExternal href="https://schoolhouse.world">
          https://schoolhouse.world
        </ShwLink>{' '}
        (together with all materials and services provided on or through the SHW website, the &quot;
        <u>SHW Platform</u>&quot;). This Agreement also incorporates by reference any additional
        terms and conditions that we post through the SHW Platform or otherwise make available to
        you.
      </Text>

      <Text style={{ fontVariant: 'small-caps' }} mb="24px">
        By using the SHW Platform, you affirm that you are of legal age to enter into this Agreement
        or, if you are not, that you have obtained parental or guardian consent to enter into this
        Agreement. In addition, users under the age of 13 are not permitted to use the SHW Platform.
        By using the SHW Platform, you affirm that you are 13 years old or older.
      </Text>

      <Text style={{ fontVariant: 'small-caps' }} mb="24px">
        This Agreement contains a mandatory arbitration provision that, as further set forth in
        Section 19 below, requires the use of arbitration on an individual basis to resolve
        disputes, rather than jury trials or any other court proceedings, or class actions of any
        kind.
      </Text>

      <ol>
        <li>
          <Text mb="24px">
            <b>Changes.</b> We may change this Agreement from time to time by notifying you of such
            changes by any reasonable means, including by posting a revised Agreement through the
            SHW Platform. Your use of the SHW Platform following notice of any such changes will
            constitute your acceptance of the changes. Any such changes will not apply to any
            dispute between you and us arising prior to the date on which we posted the revised
            Agreement incorporating such changes or otherwise notified you of such changes. The “
            <i>Last Updated</i>” legend above indicates when this Agreement was last changed.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Privacy Policy.</b>&#32;Your submission of information through the SHW Platform is
            governed by SHW&apos;s{' '}
            <ShwLink isExternal href={getAboutUrl('privacy')}>
              Privacy Policy
            </ShwLink>{' '}
            (the &quot;<u>Privacy Policy</u>&quot;). You represent and warrant that any information
            you provide in connection with the SHW Platform is and will remain accurate and
            complete, and that you will maintain and update such information as needed.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Jurisdictional Issues.</b>&#32;The SHW Platform is controlled or operated (or both)
            from the United States, and is not intended to subject SHW to any non-U.S. jurisdiction
            or law. The SHW Platform may not be appropriate or available for use in some non-U.S.
            jurisdictions. Any use of the SHW Platform is at your own risk, and you must comply with
            all applicable laws, rules and regulations in doing so. We may limit the SHW
            Platform&apos;s availability at any time, in whole or in part, to any person, geographic
            area, or jurisdiction that we choose.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Additional Program Terms.</b> Certain content, features, functionality, and programs
            provided on or through the SHW Platform may be subject to additional terms and
            conditions. These additional terms and conditions include those applicable to{' '}
            <ShwLink isExternal href={getAboutUrl('terms')}>
              Learners, Tutors, and Certifications
            </ShwLink>
            . If you access or use any such content, features, or functionality, or participate in
            any such programs, then the applicable additional terms and conditions will apply to you
            and are hereby incorporated by reference into this Agreement.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Registration; User Names and Passwords.</b> You will be required to register in order
            to access or use the SHW Platform, or participate in SHW programs. You are only
            permitted to create one account on the Platform and you must not create multiple
            accounts for any purpose, including to avoid a suspension or termination of your
            existing account. You represent and warrant that any information you provide in
            connection with registering and setting up an account for the SHW Platform, including
            age information, is and will remain accurate and complete, and that you will maintain
            and update such information as needed. We may reject, or require that you change, any
            user name, password or other information that you provide to us in registering. Your
            user name and password are for your personal use only and should be kept confidential;
            you, and not SHW, are responsible for any use or misuse of your user name or password,
            and you must promptly notify us of any confidentiality breach or unauthorized use of
            your user name or password, or your SHW Platform account.
          </Text>
        </li>

        <li>
          <Text mb="16px">
            <b>Rules of Conduct.</b> In connection with the SHW Platform, you must comply with the
            <ShwLink isExternal href={getArticleUrl('community-guidelines')}>
              Community Guidelines
            </ShwLink>
            . In addition to complying with the Community Guidelines, you must not:
          </Text>

          <Box mb="16px">
            <ul>
              <li>
                <Text mb="16px">
                  Post, transmit or otherwise make available through or in connection with the SHW
                  Platform any materials that are or may be: (a) threatening, harassing, degrading,
                  hateful or intimidating, or otherwise fail to respect the rights and dignity of
                  others; (b) defamatory, libelous, fraudulent or otherwise tortious; (c) obscene,
                  indecent, pornographic or otherwise objectionable; or (d) protected by copyright,
                  trademark, trade secret, right of publicity or privacy or any other proprietary
                  right, without the express prior written consent of the applicable owner.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Post, transmit or otherwise make available through or in connection with the SHW
                  Platform any virus, worm, Trojan horse, Easter egg, time bomb, spyware or other
                  computer code, file or program that is or is potentially harmful or invasive or
                  intended to damage or hijack the operation of, or to monitor the use of, any
                  hardware, software or equipment (each, a &quot;<u>Virus</u>&quot;).
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Use the SHW Platform for any commercial purpose, or for any purpose that is
                  fraudulent or otherwise tortious or unlawful.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Harvest or collect, or solicit information about users of the SHW Platform,
                  including any personal information.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Interfere with or disrupt the operation of the SHW Platform or the servers or
                  networks used to make the SHW Platform available, including by hacking or defacing
                  any portion of the SHW Platform; or violate any requirement, procedure or policy
                  of such servers or networks.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Restrict or inhibit any other person from using the SHW Platform.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Reproduce, modify, adapt, translate, create derivative works of, sell, rent,
                  lease, loan, timeshare, distribute or otherwise exploit any portion of (or any use
                  of) the SHW Platform except as expressly authorized herein, without SHW&apos;s
                  express prior written consent.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Reverse engineer, decompile or disassemble any portion of the SHW Platform, except
                  where such restriction is expressly prohibited by applicable law.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Remove any copyright, trademark or other proprietary rights notice from the SHW
                  Platform.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Frame or mirror any portion of the SHW Platform, or otherwise incorporate any
                  portion of the SHW Platform into any product or service, without SHW&apos;s
                  express prior written consent.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Systematically download and store SHW Platform content, or use SHW Platform
                  content for purposes of developing or training any machine learning or artificial
                  intelligence model or algorithm.
                </Text>
              </li>
              <li>
                <Text mb="16px">
                  Use any robot, spider, search/retrieval application or other manual or automatic
                  device to retrieve, index, “scrape,” “data mine” or otherwise gather SHW Platform
                  content, or reproduce or circumvent the navigational structure or presentation of
                  the SHW Platform, without SHW&apos;s express prior written consent.
                  Notwithstanding the foregoing, and subject to compliance with any instructions
                  posted in the robots.txt file located in the SHW Platform&apos;s root directory,
                  SHW grants to the operators of public search engines permission to use spiders to
                  copy materials from the SHW website for the sole purpose of (and solely to the
                  extent necessary for) creating publicly available, searchable indices of such
                  materials, but not caches or archives of such materials. SHW reserves the right to
                  revoke such permission either generally or in specific cases, at any time and
                  without notice.
                </Text>
              </li>
            </ul>
          </Box>
          <Text mb="24px">
            You are responsible for obtaining, maintaining and paying for all hardware and all
            telecommunications and other services needed to use the SHW Platform.
          </Text>
        </li>

        <li>
          <Text mb="16px">
            <b>User Submissions.</b>&#32;You and other users of the SHW Platform may make available
            certain materials (each, a &quot;<u>Submission</u>&quot;) through or in connection with
            the SHW Platform, including on profile pages or on the SHW Platform&apos;s interactive
            services, such as message boards and other forums, and chatting, commenting and other
            messaging functionality. SHW has no control over and is not responsible for any use or
            misuse (including any distribution) by any third party of Submissions.{' '}
            <Text as="span" style={{ fontVariant: 'small-caps' }}>
              If you choose to make any information publicly available through the SHW Platform, you
              do so at your own risk. Without limitation of the foregoing, you must not make any
              personally identifiable information through the SHW Platform.
            </Text>
          </Text>
          <Text mb="16px">
            For purposes of clarity, you retain ownership of your Submissions. For each Submission,
            you hereby grant to us a worldwide, royalty-free, fully paid-up, non-exclusive,
            perpetual, irrevocable, transferable and fully sublicensable (through multiple tiers)
            license, without additional consideration to you or any third party, to reproduce,
            distribute, perform and display (publicly or otherwise), create derivative works of,
            adapt, modify and otherwise use, analyze and exploit such Submission, in any format or
            media now known or hereafter developed, to the extent consistent with applicable law and
            SHW&apos;s Privacy Policy.
          </Text>
          <Text mb="16px">
            In addition, if you provide to us any ideas, proposals, suggestions or other materials
            (&quot;<u>Feedback</u>&quot;), whether related to the SHW Platform or otherwise, such
            Feedback will be deemed a Submission, and you hereby acknowledge and agree that such
            Feedback is not confidential, and that your provision of such Feedback is gratuitous,
            unsolicited and without restriction, and does not place SHW under any fiduciary or other
            obligation.
          </Text>
          <Text mb="16px">
            You represent and warrant that you have all rights necessary to grant the licenses
            granted in this section, and that your Submissions, and your provision thereof through
            and in connection with the SHW Platform, are complete and accurate, and are not
            fraudulent, tortious or otherwise in violation of any applicable law or any right of any
            third party. You further irrevocably waive any &quot;moral rights&quot; or other rights
            with respect to attribution of authorship or integrity of materials regarding each
            Submission that you may have under any applicable law under any legal theory.
          </Text>
          <Text mb="24px">
            We may (but have no obligation to) monitor, evaluate, alter or remove Submissions before
            or after they appear on the SHW Platform, or analyze your access to or use of the SHW
            Platform. We may disclose information regarding your access to and use of the SHW
            Platform, and the circumstances surrounding such access and use, to anyone for any
            reason or purpose.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Your Limited Rights.</b> Subject to your compliance with this Agreement, and solely
            for so long as you are permitted by SHW to use the SHW Platform, you may view a copy of
            any portion of the SHW website to which we provide you access under this Agreement, on
            any single device, solely for your personal, non-commercial use. If you fail to comply
            with any of the terms or conditions of this Agreement, you must immediately cease using
            the SHW Platform.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>SHW&apos;s Proprietary Rights.</b> We and our suppliers own the SHW Platform, which
            is protected by proprietary rights and laws. Our trade names, trademarks and service
            marks include SCHOOLHOUSE.WORLD and any associated logos. All trade names, trademarks,
            service marks and logos on the SHW Platform not owned by us are the property of their
            respective owners. You may not use our trade names, trademarks, service marks or logos
            in connection with any product or service that is not ours, or in any manner that is
            likely to cause confusion. Nothing contained on the SHW Platform should be construed as
            granting any right to use any trade names, trademarks, service marks or logos without
            the express prior written consent of the owner.
          </Text>
        </li>

        <li>
          <Text mb="16px">
            <b>Third Party Materials; Links.</b>&#32;Certain SHW Platform functionality may make
            available or provide access to third-party information, products, services and other
            materials, including Submissions (&quot;<u>Third Party Materials</u>&quot;), or allow
            for the routing or transmission of such Third Party Materials, including via links. By
            using such functionality, you are directing us to access, route and transmit to you the
            applicable Third Party Materials.
          </Text>
          <Text mb="16px">
            We neither control nor endorse, nor are we responsible for, any Third Party Materials,
            including the accuracy, validity, timeliness, completeness, reliability, integrity,
            quality, legality, usefulness or safety of Third Party Materials, or any intellectual
            property rights therein. Certain Third Party Materials may, among other things, be
            inaccurate, misleading or deceptive. Nothing in this Agreement will be deemed to be a
            representation or warranty by SHW with respect to any Third Party Materials. We have no
            obligation to monitor Third Party Materials, and we may block or disable access to any
            Third Party Materials (in whole or part) through the SHW Platform at any time. In
            addition, the availability of any Third Party Materials through the SHW Platform does
            not imply our endorsement of, or our affiliation with, any provider of such Third Party
            Materials, nor does such availability create any legal relationship between you and any
            such provider.
          </Text>
          <Text style={{ fontVariant: 'small-caps' }} mb="24px">
            Your use of Third Party Materials is at your own risk and is subject to any additional
            terms, conditions and policies applicable to such Third Party Materials (such as terms
            of service or privacy policies of the providers of such Third Party Materials).
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Promotions.</b>&#32;Any sweepstakes, contests, raffles, surveys, games or similar
            promotions (collectively, &quot;<u>Promotions</u>&quot;) made available through the SHW
            Platform may be governed by rules that are separate from this Agreement. If you
            participate in any Promotions, please review the applicable rules as well as our Privacy
            Policy. If the rules for a Promotion conflict with this Agreement, the Promotion rules
            will govern.
          </Text>
        </li>

        <li>
          <Text mb="16px" style={{ fontVariant: 'small-caps' }}>
            <b>Disclaimer of Warranties.</b>&#32;To the fullest extent permitted under applicable
            law: (a) the SHW Platform and any Third Party Materials are made available to you on an
            &quot;As Is,&quot; &quot;Where Is&quot; and &quot;Where Available&quot; basis, without
            any warranties of any kind, whether express, implied or statutory; and (b) SHW disclaims
            all warranties with respect to the SHW Platform and Third Party Materials, including the
            warranties of merchantability, fitness for a particular purpose, non-infringement and
            title. All disclaimers of any kind (including in this section and elsewhere in this
            Agreement) are made for the benefit of SHW and its affiliates, and its and their
            respective directors, officers, employees, agents, representatives, licensors, and
            suppliers, and the successors and assigns of any of the foregoing entities (all of the
            foregoing entities, collectively, the &quot;<u>Affiliated Entities</u>&quot;).
          </Text>
          <Text mb="24px">
            While we try to maintain the timeliness, integrity and security of the SHW Platform, we
            do not guarantee that the SHW Platform is or will remain updated, complete, correct or
            secure, or that access to the SHW Platform will be uninterrupted. The SHW Platform may
            include inaccuracies, errors and materials that violate or conflict with this Agreement.
            Additionally, third parties may make unauthorized alterations to the SHW Platform. If
            you become aware of any such alteration, contact us at{' '}
            <ShwLink href="mailto:contact@viffy.io">contact@viffy.io</ShwLink> with a description of
            such alteration and its location on the SHW Platform.
          </Text>
        </li>

        <li>
          <Text mb="24px" style={{ fontVariant: 'small-caps' }}>
            <b>Limitation of Liability.</b> To the fullest extent permitted under applicable law:
            (a) SHW will not be liable for any indirect, incidental, consequential, special,
            exemplary or punitive damages of any kind, under any contract, tort (including
            negligence), strict liability or other theory, including damages for loss of profits,
            loss of use or data, loss of other intangibles, or loss of security of Submissions
            (including unauthorized interception by third parties of any Submissions), even if
            advised in advance of the possibility of such damages or losses; (b) without limiting
            the foregoing, SHW will not be liable for damages of any kind resulting from your use of
            or inability to use the SHW Platform or from any Third Party Materials, including from
            any Virus that may be transmitted in connection therewith; (c) your sole and exclusive
            remedy for dissatisfaction with the SHW Platform or any Third Party Materials is to stop
            using the SHW Platform or such Third Party Materials; and (d) the maximum aggregate
            liability of SHW for all damages, losses and causes of action, whether in contract, tort
            (including negligence) or otherwise, will be the greater of total amount, if any, paid
            by you to SHW to use the SHW Platform and $10.00. All limitations of liability of any
            kind (including in this section and elsewhere in this Agreement) are made for the
            benefit of SHW and the other Affiliated Entities.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Indemnity.</b>&#32;To the fullest extent permitted under applicable law, you agree to
            defend, indemnify and hold harmless SHW and the other Affiliated Entities, and their
            respective successors and assigns, from and against all claims, liabilities, damages,
            judgments, awards, losses, costs, expenses and fees (including attorneys&apos; fees)
            arising out of or relating to (a) your use of, or activities in connection with, the SHW
            Platform (including all Submissions); and (b) any violation or alleged violation of this
            Agreement by you.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Termination.</b> This Agreement is effective until terminated. SHW may terminate or
            suspend your use of the SHW Platform at any time and without prior notice, for any or no
            reason, including if SHW believes that you have violated or acted inconsistently with
            the letter or spirit of this Agreement. Upon any such termination or suspension, your
            right to use the SHW Platform will immediately cease (and the licenses granted to you in
            Section 8 will immediately terminate), and SHW may, without liability to you or any
            third party, immediately deactivate or delete your user name, password and account, and
            all associated materials, without any obligation to provide any further access to such
            materials. The remaining provisions of this Agreement that by their nature should
            survive will survive any expiration or termination of this Agreement, including the
            licenses granted to SHW and all disclaimers, limitations of liability, indemnification
            provisions, dispute resolution provisions, and all other terms allocating liability or
            responsibility between you and SHW.
          </Text>
        </li>

        <li>
          <Text mb="16px">
            <b>Governing Law; Arbitration.</b> The terms of this Agreement are governed by the laws
            of the United States (including federal arbitration law) and the State of California,
            U.S.A., without regard to its principles of conflicts of law, and regardless of your
            location.{' '}
            <Text as="span" style={{ fontVariant: 'small-caps' }}>
              Except for disputes that qualify for small claims court, all disputes arising out of
              or related to this Agreement or any aspect of the relationship between you and SHW,
              whether based in contract, tort, statute, fraud, misrepresentation or any other legal
              theory, will be resolved through final and binding arbitration before a neutral
              arbitrator instead of in a court by a judge or jury and you agree that SHW and you are
              each waiving the right to trial by a jury. Such disputes include, without limitation,
              disputes arising out of or relating to interpretation or application of this
              arbitration provision, including the enforceability, revocability or validity of the
              arbitration provision or any portion of the arbitration provision. All such matters
              will be decided by an arbitrator and not by a court or judge.
            </Text>
          </Text>
          <Text mb="16px" style={{ fontVariant: 'small-caps' }}>
            You agree that any arbitration under this Agreement will take place on an individual
            basis; class arbitrations and class actions are not permitted and you are agreeing to
            give up the ability to participate in a class action.
          </Text>
          <Text mb="24px">
            The arbitration will be administered by the American Arbitration Association under its
            Consumer Arbitration Rules, as amended by this Agreement. The Consumer Arbitration Rules
            are available online at this page. The arbitrator will conduct hearings, if any, by
            teleconference or videoconference, rather than by personal appearances, unless the
            arbitrator determines upon request by you or by us that an in-person hearing is
            appropriate. Any in-person appearances will be held at a location which is reasonably
            convenient to both parties with due consideration of their ability to travel and other
            pertinent circumstances. If the parties are unable to agree on a location, such
            determination should be made by the AAA or by the arbitrator. The arbitrator&apos;s
            decision will follow the terms of this Agreement and will be final and binding. The
            arbitrator will have authority to award temporary, interim or permanent injunctive
            relief or relief providing for specific performance of this Agreement, but only to the
            extent necessary to provide relief warranted by the individual claim before the
            arbitrator. The award rendered by the arbitrator may be confirmed and enforced in any
            court having jurisdiction thereof. Notwithstanding any of the foregoing, nothing in this
            Agreement will preclude you from bringing issues to the attention of federal, state or
            local agencies and, if the law allows, they can seek relief against us for you.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Filtering.</b> We hereby notify you that parental control protections (such as
            computer hardware, software or filtering services) are commercially available that may
            assist you in limiting access to material that is harmful to minors. Information
            identifying current providers of such protections is available from this page. Please
            note that SHW does not endorse any of the products or services listed on such page.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Information or Complaints.</b> If you have a question or complaint regarding the SHW
            Platform, please contact us via our{' '}
            <ShwLink isExternal href={getUrl()}>
              Helpdesk
            </ShwLink>
            . You may also contact us by writing to Schoolhouse.world, 3790 EL CAMINO REAL UNIT PMB
            544, PALO ALTO, CA 94306, or by emailing{' '}
            <ShwLink isExternal href="mailto:notices@schoolhouse.world">
              notices@schoolhouse.world
            </ShwLink>
            . Please note that e-mail communications will not necessarily be secure; accordingly you
            should not include credit card information or other sensitive information in your e-mail
            correspondence with us. California residents may reach the Complaint Assistance Unit of
            the Division of Consumer Services of the California Department of Consumer Affairs by
            mail at 1625 North Market Blvd., Sacramento, CA 95834, or by telephone at (916) 445-1254
            or (800) 952-5210.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Copyright Infringement Claims.</b>&#32;The Digital Millennium Copyright Act of 1998
            (the &quot;<u>DMCA</u>&quot;) provides recourse for copyright owners who believe that
            material appearing on the Internet infringes their rights under U.S. copyright law. If
            you believe in good faith that materials available on the SHW Platform infringe your
            copyright, you (or your agent) may send to SHW a written notice by mail or e-mail,
            requesting that SHW remove such material or block access to it. If you believe in good
            faith that someone has wrongly filed a notice of copyright infringement against you, the
            DMCA permits you to send to SHW a counter-notice. Notices and counter-notices must meet
            the then-current statutory requirements imposed by the DMCA. See statutory requirements
            imposed by the DMCA. See{' '}
            <ShwLink isExternal href="http://www.copyright.gov/">
              http://www.copyright.gov/
            </ShwLink>{' '}
            for details. Notices and counter-notices must be sent in writing to SHW&apos;s
            designated agent, Schoolhouse.world, as follows: by mail to Schoolhouse World Inc, 3790
            El Camino Real, Unit #544; or by e-mail to{' '}
            <ShwLink href="mailto:copyright@schoolhouse.world">copyright@schoolhouse.world</ShwLink>
            . We suggest that you consult your legal advisor before filing a DMCA notice or
            counter-notice.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Export Controls.</b>&#32;You are responsible for complying with United States export
            controls and for any violation of such controls, including any United States embargoes
            or other federal rules and regulations restricting exports. You represent and warrant
            that you are not (a) located in, or a resident or a national of, any country subject to
            a U.S. government embargo or other restriction, or that has been designated by the U.S.
            government as a &quot;terrorist supporting&quot; country; or (b) on any of the U.S.
            government lists of restricted end users.
          </Text>
        </li>

        <li>
          <Text mb="24px">
            <b>Miscellaneous.</b>&#32;This Agreement does not, and will not be construed to, create
            any partnership, joint venture, employer-employee, agency or franchisor-franchisee
            relationship between you and SHW. If any provision of this Agreement is found to be
            unlawful, void or for any reason unenforceable, that provision will be deemed severable
            from this Agreement and will not affect the validity and enforceability of any remaining
            provision. You may not assign, transfer or sublicense any or all of your rights or
            obligations under this Agreement without our express prior written consent. We may
            assign, transfer or sublicense any or all of our rights or obligations under this
            Agreement without restriction. No waiver by either party of any breach or default under
            this Agreement will be deemed to be a waiver of any preceding or subsequent breach or
            default. Any heading, caption or section title contained herein is for convenience only,
            and in no way defines or explains any section or provision. All terms defined in the
            singular will have the same meanings when used in the plural, where appropriate and
            unless otherwise specified. Any use of the term &quot;including&quot; or variations
            thereof in this Agreement will be construed as if followed by the phrase &quot;without
            limitation.&quot; This Agreement, including any terms and conditions incorporated
            herein, is the entire agreement between you and SHW relating to the subject matter
            hereof, and supersedes any and all prior or contemporaneous written or oral agreements
            or understandings between you and SHW relating to such subject matter. Notices to you
            (including notices of changes to this Agreement) may be made via posting to the SHW
            Platform or by e-mail (including in each case via links), or by regular mail. Without
            limitation, a printed version of this Agreement and of any notice given in electronic
            form will be admissible in judicial or administrative proceedings based upon or relating
            version of this Agreement and of any notice given in electronic form will be admissible
            in judicial or administrative proceedings based upon or relating to this Agreement to
            the same extent and subject to the same conditions as other business documents and
            records originally generated and maintained in printed form. SHW will not be responsible
            for any failure to fulfill any obligation due to any cause beyond its control.
          </Text>
        </li>
      </ol>

      <Box mt="32px" borderTop="1px" borderColor="border" pt="16px">
        <Text color="fg.muted" fontSize="sm">
          © 2024 Schoolhouse.World, Inc. unless otherwise noted. All rights reserved.
        </Text>
      </Box>
    </Container>
  </Layout>
)

const TermsOfServicePage = makePage(TermsOfService, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TermsOfServicePage)
export default TermsOfServicePage
